// src/components/risk/ClientRiskDetailDrawer.jsx
// Slide-out drawer with full client risk details and intervention options

import React, { useState, useEffect } from 'react';
import {
  X,
  Phone,
  MessageSquare,
  Mail,
  ExternalLink,
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertTriangle,
  User,
  Calendar,
  FileText,
  ChevronRight
} from 'lucide-react';
import RiskScoreGauge from './RiskScoreGauge';
import RiskScoreBreakdown from './RiskScoreBreakdown';
import RecommendedActionsCard from './RecommendedActionsCard';
import { getRiskCategory } from './constants';

const BASE = process.env.REACT_APP_API_URL || "";

const ClientRiskDetailDrawer = ({
  client,
  isOpen,
  onClose,
  onIntervention,
  onNavigateToClient,
  onRefreshScore
}) => {
  const [loading, setLoading] = useState(false);
  const [riskData, setRiskData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [commsHistory, setCommsHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch detailed risk data when client changes
  useEffect(() => {
    if (client?.id && isOpen) {
      fetchClientDetails();
    }
  }, [client?.id, isOpen]);

  const fetchClientDetails = async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      const [riskRes, tasksRes, commsRes] = await Promise.all([
        fetch(`${BASE}/churn-prediction/score/${client.id}`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${BASE}/tasks?client_id=${client.id}&status=pending&limit=10`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ tasks: [] })),
        fetch(`${BASE}/clients/${client.id}/comms?limit=10`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ data: [] }))
      ]);

      if (riskRes.ok && riskRes.score) {
        setRiskData({
          score: riskRes.score.finalScore ?? riskRes.score.score ?? client.risk_score ?? 0,
          factors: riskRes.score.allFactors || [],
          categoryBreakdown: riskRes.score.categoryScores || {},
          autoTrigger: riskRes.score.auto100Reason || null,
          lastScored: riskRes.score.lastScored,
          riskCategory: riskRes.score.riskCategory,
          isNewClient: riskRes.score.isNewClient || false,
          newClientBaselineApplied: riskRes.score.newClientBaselineApplied || false
        });
      }
      setTasks(tasksRes.tasks || []);
      setCommsHistory(commsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch client details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await onRefreshScore?.(client.id);
      await fetchClientDetails();
    } finally {
      setLoading(false);
    }
  };

  // Handle marking a risk factor as resolved
  const handleResolveFactor = async (factor, clientId) => {
    if (!clientId || !factor) return;

    try {
      const res = await fetch(`${BASE}/churn-prediction/resolve-factor`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId,
          factorCode: factor.code || factor.name,
          factorName: factor.name
        })
      });

      if (res.ok) {
        // Refresh the risk data to show updated factors
        await fetchClientDetails();
      } else {
        console.error('Failed to resolve factor');
      }
    } catch (err) {
      console.error('Error resolving factor:', err);
    }
  };

  if (!isOpen) return null;

  const category = getRiskCategory(riskData?.score ?? client?.risk_score ?? 0);
  const clientName = `${client?.first_name || ''} ${client?.last_name || ''}`.trim() || 'Unknown Client';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full sm:w-[32rem] md:w-[40rem] lg:w-[48rem] bg-slate-900 border-l border-slate-700 z-50 flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex-shrink-0 bg-slate-800 border-b border-slate-700 p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {/* Client avatar */}
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold"
                style={{ backgroundColor: category.color }}
              >
                {(client?.first_name || '?')[0]}{(client?.last_name || '?')[0]}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{clientName}</h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span
                    className="px-2 py-0.5 rounded text-xs font-medium text-white"
                    style={{ backgroundColor: category.color }}
                  >
                    {category.name} Risk
                  </span>
                  {riskData?.isNewClient && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-500 text-slate-900">
                      New Client - 90 Day Period
                    </span>
                  )}
                  <span className="text-sm text-slate-400">
                    Score: {Math.round(riskData?.score ?? client?.risk_score ?? 0)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                title="Refresh score"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Quick action buttons */}
          <div className="flex gap-2 mt-4">
            {client?.phone && (
              <>
                <button
                  onClick={() => onIntervention?.('call', client)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  <span className="hidden sm:inline">Call</span>
                </button>
                <button
                  onClick={() => onIntervention?.('sms', client)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="hidden sm:inline">SMS</span>
                </button>
              </>
            )}
            {client?.email && (
              <button
                onClick={() => onIntervention?.('email', client)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">Email</span>
              </button>
            )}
            <button
              onClick={() => onNavigateToClient?.(client.id)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="hidden sm:inline">Profile</span>
            </button>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="flex-shrink-0 border-b border-slate-700 bg-slate-800/50">
          <div className="flex">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'factors', label: 'Risk Factors' },
              { id: 'tasks', label: `Tasks (${tasks.length})` },
              { id: 'history', label: 'History' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-amber-400 border-b-2 border-amber-400'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Risk gauge */}
                  <div className="flex justify-center py-4">
                    <RiskScoreGauge
                      score={riskData?.score ?? client?.risk_score ?? 0}
                      size="lg"
                      showLabel
                      showCategory
                    />
                  </div>

                  {/* Action needed */}
                  <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <AlertTriangle className="w-4 h-4" />
                      Action Required
                    </div>
                    <div className="text-lg font-medium" style={{ color: category.color }}>
                      {category.action}
                    </div>
                  </div>

                  {/* Recommended actions */}
                  <RecommendedActionsCard
                    factors={riskData?.factors || []}
                    score={riskData?.score ?? client?.risk_score ?? 0}
                    onActionClick={(action) => onIntervention?.(action, client)}
                  />

                  {/* Client info */}
                  <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                    <h4 className="text-sm font-medium text-slate-400 mb-3">Contact Info</h4>
                    <div className="space-y-2">
                      {client?.phone && (
                        <div className="flex items-center gap-2 text-white">
                          <Phone className="w-4 h-4 text-slate-500" />
                          {client.phone}
                        </div>
                      )}
                      {client?.email && (
                        <div className="flex items-center gap-2 text-white">
                          <Mail className="w-4 h-4 text-slate-500" />
                          {client.email}
                        </div>
                      )}
                      {client?.date_of_birth && (
                        <div className="flex items-center gap-2 text-white">
                          <Calendar className="w-4 h-4 text-slate-500" />
                          DOB: {new Date(client.date_of_birth).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Factors Tab */}
              {activeTab === 'factors' && (
                <RiskScoreBreakdown
                  score={riskData?.score ?? client?.risk_score ?? 0}
                  factors={riskData?.factors || []}
                  categoryBreakdown={riskData?.categoryBreakdown || {}}
                  autoTrigger={riskData?.autoTrigger}
                  onResolveFactor={handleResolveFactor}
                  clientId={client?.id}
                />
              )}

              {/* Tasks Tab */}
              {activeTab === 'tasks' && (
                <div className="space-y-3">
                  {tasks.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No pending tasks</p>
                    </div>
                  ) : (
                    tasks.map(task => (
                      <div
                        key={task.id}
                        className="p-4 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-slate-600 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-white">{task.title}</h4>
                            {task.description && (
                              <p className="text-sm text-slate-400 mt-1">{task.description}</p>
                            )}
                          </div>
                          {task.due_date && (
                            <div className="flex items-center gap-1 text-xs text-slate-500">
                              <Clock className="w-3 h-3" />
                              {new Date(task.due_date).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* History Tab */}
              {activeTab === 'history' && (
                <div className="space-y-3">
                  {commsHistory.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No communication history</p>
                    </div>
                  ) : (
                    commsHistory.map((comm, idx) => (
                      <div
                        key={comm.id || idx}
                        className="p-4 rounded-lg bg-slate-800/50 border border-slate-700"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {comm.type === 'sms' && <MessageSquare className="w-4 h-4 text-blue-400" />}
                            {comm.type === 'call' && <Phone className="w-4 h-4 text-green-400" />}
                            {comm.type === 'email' && <Mail className="w-4 h-4 text-purple-400" />}
                            <span className="text-sm font-medium text-white capitalize">{comm.type}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              comm.direction === 'inbound' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
                            }`}>
                              {comm.direction}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500">
                            {comm.created_at && new Date(comm.created_at).toLocaleString()}
                          </span>
                        </div>
                        {comm.body && (
                          <p className="text-sm text-slate-400 line-clamp-2">{comm.body}</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ClientRiskDetailDrawer;
